import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchSpaceAnalytics, fetchSpaces } from '../../services/spaceService';

const analyticsOwnerKey = (targetUserId) => (targetUserId ? String(targetUserId) : 'self');

export const loadOrgSpaces = createAsyncThunk(
  'spaces/loadOrgSpaces',
  async ({ force } = {}, { getState }) => {
    const current = getState().spaces;
    if (!force && current.fetchedOnce) {
      return current.list;
    }
    return fetchSpaces();
  }
);

export const loadSpaceAnalytics = createAsyncThunk(
  'spaces/loadSpaceAnalytics',
  async ({ targetUserId, force } = {}, { getState }) => {
    const key = analyticsOwnerKey(targetUserId);
    const cached = getState().spaces.analyticsByOwner[key];
    if (!force && cached?.loaded) {
      return { key, rows: cached.rows || [] };
    }
    const rows = await fetchSpaceAnalytics(targetUserId);
    return { key, rows: Array.isArray(rows) ? rows : [] };
  }
);

const rowsToMap = (rows) => {
  const next = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.spaceKey) next[String(row.spaceKey)] = row;
  });
  return next;
};

const initialState = {
  list: [],
  loaded: false,
  fetchedOnce: false,
  analyticsByOwner: {},
};

const spaceSlice = createSlice({
  name: 'spaces',
  initialState,
  reducers: {
    upsertSpace: (state, action) => {
      const space = action.payload;
      if (!space?.id) return;
      const next = Array.isArray(state.list) ? [...state.list] : [];
      const index = next.findIndex((item) => item.id === space.id);
      if (index >= 0) next[index] = { ...next[index], ...space };
      else next.push(space);
      state.list = next;
      state.loaded = true;
      state.fetchedOnce = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadOrgSpaces.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload) ? action.payload : [];
        state.loaded = true;
        state.fetchedOnce = true;
      })
      .addCase(loadSpaceAnalytics.fulfilled, (state, action) => {
        const key = action.payload?.key || 'self';
        const rows = action.payload?.rows || [];
        state.analyticsByOwner[key] = {
          loaded: true,
          rows,
          byKey: rowsToMap(rows),
        };
      });
  },
});

export const { upsertSpace } = spaceSlice.actions;
export default spaceSlice.reducer;
